import type { FilterConfig, FilterType } from './types';

import { useGetFilterOptionsQuery } from '@/store/reports/ats/queries';
import { JobListMenu } from '@/components/ats/filters/components/UiItems/JobList';
import { useGetAllJobsListQuery } from '@/store/query/ats-jobs';
import { selectOutreachesListAll } from '@/store/outreaches/selectors';
import { useSelector } from 'react-redux';
import { selectOutreachUsers, selectUsers } from '@/store/users/selectors';
import { REJECT_REASON_GROUPS } from '@/pages/settings/ats/reject-reasons/rejectReasonsHelper';
import { KText, KTag } from '@/ui-library/components';
import React from 'react';
import { requisitionStatusConfig } from '@/pages/requisitions/constants';
import { jobStatusConfig } from '@/pages/ats-job-creation/utils/constants';
import { components } from 'react-select';
import { Flex } from '@chakra-ui/react';

// Define the desired order for application stages
const APPLICATION_STAGE_ORDER = [
  'Prospect',
  'Application review',
  'Interview',
  'Offer',
  'Hired',
];

const GroupHeading = (props: { children?: React.ReactNode }) => {
  return React.createElement(KText, {
    size: 'sm-semi-bold',
    mb: '6px',
    ml: '4px',
    children: props.children,
  });
};

const surveyTypeConfig: Record<
  string,
  { color: string; bg: string; label: string }
> = {
  CANDIDATE: {
    color: 'pastel-5-dark',
    bg: 'pastel-5-light',
    label: 'Candidate survey',
  },
  HIRING_MANAGER: {
    color: 'pastel-4-dark',
    bg: 'pastel-4-light',
    label: 'HM survey',
  },
};

const SurveyOption = (props: {
  data?: Record<string, unknown>;
  selectProps?: { extraProps?: Record<string, unknown> };
}) => {
  const { data, selectProps = {} } = props;
  const { extraProps = {} } = selectProps;
  const { labelKey = 'label', tagKey } = extraProps;
  const labelKeyStr = String(labelKey);
  const tagKeyStr =
    tagKey !== null && tagKey !== undefined ? String(tagKey) : '';

  const label = data?.[labelKeyStr];
  const tagValue = tagKeyStr ? data?.[tagKeyStr] : null;
  const tagConfig =
    tagValue !== null && tagValue !== undefined && String(tagValue) !== ''
      ? surveyTypeConfig[String(tagValue)]
      : null;

  return React.createElement(
    components.Option,
    props,
    React.createElement(
      Flex,
      { alignItems: 'center', gap: '8px', width: '100%' },
      React.createElement(KText, {
        size: 'sm-regular',
        color: 'secondary-700',
        children:
          label !== null && label !== undefined ? String(label) : undefined,
      }),
      tagConfig &&
        React.createElement(KTag, {
          color: tagConfig.color,
          bgColor: tagConfig.bg,
          value: tagConfig.label,
        })
    )
  );
};

const groupOptions = (options) => {
  const groups = {};

  // Group options based on the "group" property
  options?.forEach((option) => {
    const groupName = option.group;
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    // Only add enabled and not deleted items
    if (option?.enabled && !option?.deleted) {
      groups[groupName].push({
        ...option,
        title: option.reason, // Add label field for display
      });
    }
  });

  // Convert the grouped options into the desired format and filter out empty groups
  const groupedOptions = Object.entries(groups)
    .filter(([_, groupOptions]) => (groupOptions as unknown[]).length > 0) // Filter out empty groups
    .map(([label, groupOptions]) => ({
      label: REJECT_REASON_GROUPS?.find((rejRsn) => rejRsn?.id === label)
        ?.label,
      options: groupOptions,
    }));

  return groupedOptions;
};

export const FILTER_CONFIG: Record<
  FilterType,
  {
    attribute: string;
    scope?: string;
    name: string;
    labelField?: string;
    valueField?: string;
    tooltip?: string;
    tagKey?: string;
    components?: Record<string, unknown>;
    operatorType?: 'string' | 'number' | 'boolean';
    excludeFilterCondition?: boolean;
    isMulti?: boolean;
  }
> = {
  appl_location: {
    name: 'Office',
    attribute: 'office',
  },
  job_location: {
    name: 'Location (on job)',
    attribute: 'office',
  },
  appl_contact_source: {
    name: 'Source (on application)',
    attribute: 'appl_contact_source',
    scope: 'level_2',
  },
  contact_source: {
    name: 'Source (on contact)',
    attribute: 'contact_source',
    scope: 'level_2',
  },
  appl_stage: {
    name: 'Application stage',
    attribute: 'job_milestones',
    labelField: 'title',
  },
  appl_credited_to: {
    name: 'Application credited to',
    attribute: 'users',
    scope: 'appl_credited_to',
  },
  contact_credited_to: {
    name: 'Contact credited to',
    attribute: 'users',
    scope: 'contact_credited_to',
  },
  department: {
    name: 'Department',
    attribute: 'department',
  },
  job_name: {
    name: 'Job name',
    attribute: 'job',
    labelField: 'title',
  },
  job_status: {
    name: 'Job status',
    attribute: 'job_status',
    labelField: 'title',
  },
  job_type: {
    name: 'Job type',
    attribute: 'job_type',
    labelField: 'title',
  },
  offer_creator: {
    name: 'Offer creator',
    attribute: 'users',
    scope: 'offer_creator',
  },
  offer_rejection_reason: {
    name: 'Offer rejection reason',
    attribute: 'rejection_reasons',
    labelField: 'title',
  },
  primary_hm: {
    name: 'Primary hiring manager',
    attribute: 'users',
    scope: 'hiring_manager',
  },
  primary_recruiter: {
    name: 'Primary recruiter',
    attribute: 'users',
    scope: 'recruiter',
  },
  rejected_stage: {
    name: 'Rejected stage',
    attribute: 'job_milestones',
    labelField: 'title',
  },
  rejection_reason: {
    name: 'Rejection reason',
    attribute: 'rejection_reasons',
    labelField: 'title',
  },
  rejection_type: {
    name: 'Rejection type',
    attribute: 'interview_rejection_type',
    labelField: 'title',
  },

  interview_type: {
    name: 'Interview type',
    attribute: 'interview_type',
    labelField: 'title',
  },
  duration_bucket: {
    name: 'Interview duration',
    attribute: 'interview_duration_bucket',
    labelField: 'title',
  },
  close_reason: {
    name: 'Job close reason',
    attribute: 'close_reason',
    labelField: 'title',
  },
  gender: {
    name: 'Gender',
    attribute: 'eeoc_gender',
    labelField: 'title',
  },
  race: {
    name: 'Race',
    attribute: 'eeoc_race',
    labelField: 'title',
  },
  veteran_status: {
    name: 'Veteran status',
    attribute: 'eeoc_veteran_status',
    labelField: 'title',
  },
  disability_status: {
    name: 'Disability status',
    attribute: 'eeoc_disability_status',
    labelField: 'title',
  },
  experience_bucket: {
    name: 'Experience buckets',
    attribute: 'experience_bucket',
    labelField: 'title',
  },
  recruiter_id: {
    name: 'Recruiter',
    attribute: 'users',
    scope: 'recruiter',
  },
  flow_id: {
    name: 'Flow',
    attribute: 'flow',
  },
  rejected_by_type: {
    name: 'Rejected by',
    attribute: 'rejected_by_type',
    labelField: 'title',
  },

  user_role: {
    name: 'Job role',
    attribute: 'user_role',
    labelField: 'title',
  },

  associated_job_name: {
    name: 'Associated job name',
    attribute: 'job',
    labelField: 'title',
  },

  work_type: {
    name: 'Work type',
    attribute: 'work_type',
    labelField: 'title',
  },
  requisition_type: {
    name: 'Requisition type',
    attribute: 'requisition_type',
    labelField: 'title',
  },
  requisition_status: {
    name: 'Requisition status',
    attribute: 'requisition_status',
    labelField: 'title',
  },
  recruiter: {
    name: 'Recruiter',
    attribute: 'users',
    scope: 'recruiter',
  },
  hiring_manager: {
    name: 'Hiring manager',
    attribute: 'users',
    scope: 'hiring_manager',
  },
  office: {
    name: 'Office',
    attribute: 'office',
  },
  associated_job_status: {
    name: 'Associated job status',
    attribute: 'associated_job_status',
    labelField: 'title',
  },

  survey_name: {
    name: 'Survey name',
    attribute: 'survey_name',
    tagKey: 'type',
    labelField: 'name',
    tooltip: 'This filter applies to survey blocks only',
    components: {
      Option: SurveyOption,
    },
  },
  time_in_stage: {
    name: 'Time in stage',
    attribute: 'time_in_stage',
    labelField: 'title',
    operatorType: 'number',
  },
  is_overdue: {
    name: 'Overdue candidates',
    attribute: 'is_overdue',
    labelField: 'title',
    excludeFilterCondition: true,
    isMulti: false,
  },
  application_creator: {
    name: 'Application creator',
    attribute: 'users',
    scope: 'application_creator',
  },
  users: {
    name: 'User',
    attribute: 'users',
    labelField: 'name',
  },
  requester: {
    name: 'Requisition created by',
    attribute: 'requester',
    labelField: 'name',
  },
  created_by: {
    name: 'Job created by',
    attribute: 'users',
    scope: 'created_by',
  },
  hired_by: {
    name: 'Hired by',
    attribute: 'hired_by',
    labelField: 'name',
  },
  scheduled_by: {
    name: 'Scheduled by',
    attribute: 'scheduled_by',
    labelField: 'name',
  },
  rejected_by: {
    name: 'Rejected by',
    attribute: 'rejected_by',
    labelField: 'name',
  },
  offer_by: {
    name: 'Offer created by',
    attribute: 'offer_by',
    labelField: 'name',
  },
  contact_created_by: {
    name: 'Contact created by',
    attribute: 'contact_created_by',
    labelField: 'name',
  },
  bgv_risk_level: {
    name: 'Fraud status',
    attribute: 'bgv_risk_level',
    labelField: 'title',
  },
};

export interface UseFilterConfigOptions {
  reportType?: string;
}

export const useFilterConfig = (
  filter: FilterType,
  configOptions?: UseFilterConfigOptions
): FilterConfig => {
  const outreachUsers = useSelector(selectOutreachUsers);
  const flows = useSelector(selectOutreachesListAll);
  const users = useSelector(selectUsers);

  const isContact = configOptions?.reportType === 'contacts';
  const isLivePipelineReport =
    configOptions?.reportType === 'live_job_pipeline';
  const attribute = filter && FILTER_CONFIG[filter]?.attribute;
  const scope = filter && FILTER_CONFIG[filter]?.scope;

  // Filters that should skip the API call (they use static options)
  const filtersToSkipApi = [
    'requisition_type',
    'requisition_status',
    'work_type',
    'recruiter_id',
    'flow_id',
    'associated_job_name',
    'job_name',
    'created_by',
    'associated_job_status',
    'requester',
    'time_in_stage',
    'application_creator',
    'is_overdue',
    'offer_by',
    'rejected_by',
    'scheduled_by',
    'hired_by',
    'contact_created_by',
  ];

  const {
    data: options,
    isLoading,
    isFetching,
  } = useGetFilterOptionsQuery(
    {
      attribute: attribute || '',
      scope: scope || '',
    },
    {
      skip: !attribute || filtersToSkipApi.includes(filter),
    }
  );

  const isFilterLoading = isLoading || isFetching;

  const { data: atsJobs = [], isLoading: isJobsLoading } =
    useGetAllJobsListQuery(
      {
        params: {
          scope: 'viewable',
          ...(isLivePipelineReport && {
            filter_by: { job_statuses: ['published'] },
          }),
          ...(isContact && {
            filter_by: { job_statuses: ['published', 'archived', 'closed'] },
          }),
        },
      },
      {
        skip: filter !== 'job_name' && filter !== 'associated_job_name',
      }
    );

  const {
    name,
    labelField = 'name',
    valueField = 'id',
    tagKey,
    tooltip,
    components: filterComponents,
    operatorType,
    excludeFilterCondition,
    isMulti = true,
  } = FILTER_CONFIG[filter];

  // Check if filter exists in FILTER_CONFIG
  if (!filter || !FILTER_CONFIG[filter]) {
    return {
      type: 'static',
      id: filter as FilterType,
      label: 'Unknown Filter',
      isLoading: false,
      labelField: 'name',
      valueField: 'id',
      options: [],
      operatorType: 'string',
    };
  }

  if (filter === 'time_in_stage') {
    return {
      type: 'static',
      id: filter,
      label: 'Time in stage',
      isLoading: isFilterLoading,
      labelField: 'label',
      valueField: 'id',
      operatorType: 'number',
      options: [
        { id: 1, label: '1 day' },
        { id: 2, label: '2 days' },
        { id: 3, label: '3 days' },
        { id: 4, label: '4 days' },
        { id: 5, label: '5 days' },
        { id: 6, label: '6 days' },
        { id: 7, label: '7 days' },
        { id: 8, label: '8 days' },
        { id: 9, label: '9 days' },
        { id: 10, label: '10 days' },
        { id: 11, label: '11 days' },
        { id: 12, label: '12 days' },
        { id: 13, label: '13 days' },
        { id: 14, label: '14 days' },
        { id: 15, label: '15 days' },
      ],
    };
  }

  if (filter === 'requisition_type') {
    return {
      type: 'static',
      id: filter,
      label: 'Requisition type',
      isLoading: isFilterLoading,
      labelField: 'label',
      valueField: 'id',
      options: [
        { id: 'new_hire', label: 'New hire' },
        { id: 'backfill', label: 'Backfill' },
        { id: 'internal', label: 'Internal' },
      ],
    };
  }

  if (filter === 'is_overdue') {
    return {
      type: 'static',
      id: filter,
      label: 'Overdue candidates',
      isLoading: isFilterLoading,
      labelField: 'label',
      valueField: 'id',
      isMulti,
      options: [
        { id: 'yes', label: 'Yes' },
        { id: 'no', label: 'No' },
      ],
      excludeFilterCondition,
    };
  }

  if (filter === 'requisition_status') {
    return {
      type: 'static',
      id: filter,
      label: 'Requisition status',
      isLoading: isFilterLoading,
      labelField: 'label',
      valueField: 'value',
      options: Object.values(requisitionStatusConfig).map((status) => ({
        id: status.id,
        value: status.id,
        label: status.label,
      })),
    };
  }

  if (filter === 'associated_job_status') {
    return {
      type: 'static',
      id: filter,
      label: 'Associated job status',
      isLoading: isFilterLoading,
      labelField: 'label',
      valueField: 'id',
      options: Object.values(jobStatusConfig).map((status) => ({
        id: status.id,
        value: status.id,
        label: status.label,
      })),
    };
  }

  if (filter === 'work_type') {
    return {
      type: 'static',
      id: filter,
      label: 'Work type',
      isLoading: isFilterLoading,
      labelField: 'label',
      valueField: 'id',
      options: [
        { id: 'full_time', label: 'Full time' },
        { id: 'part_time', label: 'Part time' },
        { id: 'contract', label: 'Contract' },
        { id: 'internship', label: 'Internship' },
        { id: 'temporary', label: 'Temporary' },
        { id: 'seasonal', label: 'Seasonal' },
      ],
      excludeFilterCondition,
    };
  }

  if (filter === 'recruiter_id') {
    return {
      type: 'static',
      id: filter,
      label: name,
      isLoading: isFilterLoading,
      labelField,
      valueField,
      options: outreachUsers,
    };
  }
  if (filter === 'flow_id') {
    return {
      type: 'static',
      id: filter,
      label: name,
      isLoading: isFilterLoading,
      labelField: 'title',
      valueField,
      options: flows,
    };
  }

  if (!attribute) return null;

  if (filter === 'job_name' || filter === 'associated_job_name') {
    return {
      type: 'static',
      id: filter,
      label: name,
      isLoading: isJobsLoading,
      labelField: 'label',
      valueField: 'id',
      options: atsJobs?.map((item) => ({
        id: item?.id,
        value: item?.id,
        label: item?.name,
        status: item?.status || 'published',
        department: item?.department,
        locations: item?.locations || [],
      })),
      ...(isLivePipelineReport && { showToggle: false }),
      newStyles: [
        {
          type: 'menu',
          newStyles: {
            padding: '0px !important',
            borderRadius: '6px !important',
            border: '1px solid #EDEDED',
            borderColor: 'border-color',
            boxShadow: 'none',
          },
        },
      ],
      components: {
        Menu: JobListMenu,
        IndicatorSeparator: () => null,
      },
    };
  }

  if (
    filter === 'requester' ||
    filter === 'application_creator' ||
    filter === 'created_by' ||
    filter === 'hired_by' ||
    filter === 'scheduled_by' ||
    filter === 'rejected_by' ||
    filter === 'offer_by' ||
    filter === 'contact_created_by'
  ) {
    return {
      type: 'static',
      id: filter,
      label: name,
      isLoading: isFilterLoading,
      labelField: 'label',
      valueField: 'id',
      options:
        filter === 'application_creator'
          ? [
              {
                id: null,
                value: null,
                label: 'Inbound',
              },
              ...(users?.map((user) => ({
                id: user?.id,
                value: user?.id,
                label: user?.name,
              })) || []),
            ]
          : users?.map((user) => ({
              id: user?.id,
              value: user?.id,
              label: user?.name,
            })) || [],

      excludeFilterCondition,
    };
  }

  if (filter === 'rejection_reason') {
    return {
      type: 'static',
      id: filter,
      label: name,
      isLoading: isFilterLoading,
      labelField,
      valueField,
      options: groupOptions(options),
      components: {
        GroupHeading,
        IndicatorSeparator: () => null,
      },
    };
  }

  if (filter === 'appl_stage') {
    // Sort options based on the defined order
    const sortedOptions = [...(options || [])].sort((a, b) => {
      const titleA = a?.title || '';
      const titleB = b?.title || '';
      const indexA = APPLICATION_STAGE_ORDER.indexOf(titleA);
      const indexB = APPLICATION_STAGE_ORDER.indexOf(titleB);

      // If both stages are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in the order array, it comes first
      if (indexA !== -1) return -1;
      // If only B is in the order array, it comes first
      if (indexB !== -1) return 1;
      // If neither is in the order array, maintain original order
      return 0;
    });

    return {
      type: 'static',
      id: filter,
      label: name,
      isLoading: isFilterLoading,
      labelField,
      valueField,
      components: filterComponents,
      options: sortedOptions,
      tagKey,
      tooltip,
      excludeFilterCondition,
    };
  }

  return {
    type: 'static',
    id: filter,
    label: name,
    isLoading: isFilterLoading,
    labelField,
    valueField,
    components: filterComponents,
    options,
    tagKey,
    tooltip,
    operatorType,
    excludeFilterCondition: excludeFilterCondition || false,
  };
};
